import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  capitalizeFirst,
  parseWholeCount,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A beat-by-beat story outline in one of four structures, each beat carrying an
 * original prompt built from the writer's protagonist, goal, obstacle, setting and theme:
 *
 * - **Three-act structure** — setup, confrontation, resolution, with the turning points
 *   at the conventional quarter marks (a convention, not a rule).
 * - **The Hero's Journey** — Christopher Vogler's twelve stages (*The Writer's Journey*),
 *   his condensation of Joseph Campbell's monomyth, grouped into his three acts.
 * - **Save the Cat** — Blake Snyder's fifteen-beat sheet (*Save the Cat!*, 2005), placed
 *   by his page numbers for a 110-page screenplay and scaled to the writer's length.
 * - **Freytag's pyramid** — Gustav Freytag's five-part dramatic arc (*Die Technik des
 *   Dramas*, 1863), with his "exciting force" as the inciting incident.
 */

export type StoryStructure =
  | "three-act"
  | "heros-journey"
  | "save-the-cat"
  | "freytag";

export type LengthUnit = "words" | "pages";

export type StoryAct = "one" | "two" | "three";

export interface BeatSpec {
  id: string;
  act?: StoryAct;
  /** Start of the beat: a fraction of the story, or a Snyder page for Save the Cat. */
  from?: number;
  /** End of a beat that spans a stretch. */
  to?: number;
}

/** Snyder's page numbers assume a 110-page screenplay. */
export const SNYDER_PAGES = 110;

export const STORY_BEATS: Record<StoryStructure, readonly BeatSpec[]> = {
  "three-act": [
    { id: "ta-setup", act: "one", from: 0, to: 0.1 },
    { id: "ta-inciting", act: "one", from: 0.1 },
    { id: "ta-plot-one", act: "one", from: 0.25 },
    { id: "ta-rising", act: "two", from: 0.25, to: 0.5 },
    { id: "ta-midpoint", act: "two", from: 0.5 },
    { id: "ta-complications", act: "two", from: 0.5, to: 0.75 },
    { id: "ta-plot-two", act: "two", from: 0.75 },
    { id: "ta-climax", act: "three", from: 0.9 },
    { id: "ta-resolution", act: "three", from: 0.95, to: 1 },
  ],
  "heros-journey": [
    { id: "hj-ordinary-world", act: "one" },
    { id: "hj-call", act: "one" },
    { id: "hj-refusal", act: "one" },
    { id: "hj-mentor", act: "one" },
    { id: "hj-threshold", act: "one" },
    { id: "hj-tests", act: "two" },
    { id: "hj-approach", act: "two" },
    { id: "hj-ordeal", act: "two" },
    { id: "hj-reward", act: "two" },
    { id: "hj-road-back", act: "three" },
    { id: "hj-resurrection", act: "three" },
    { id: "hj-return", act: "three" },
  ],
  "save-the-cat": [
    { id: "stc-opening-image", act: "one", from: 1 },
    { id: "stc-theme-stated", act: "one", from: 5 },
    { id: "stc-set-up", act: "one", from: 1, to: 10 },
    { id: "stc-catalyst", act: "one", from: 12 },
    { id: "stc-debate", act: "one", from: 12, to: 25 },
    { id: "stc-break-into-two", act: "two", from: 25 },
    { id: "stc-b-story", act: "two", from: 30 },
    { id: "stc-fun-and-games", act: "two", from: 30, to: 55 },
    { id: "stc-midpoint", act: "two", from: 55 },
    { id: "stc-bad-guys", act: "two", from: 55, to: 75 },
    { id: "stc-all-is-lost", act: "two", from: 75 },
    { id: "stc-dark-night", act: "two", from: 75, to: 85 },
    { id: "stc-break-into-three", act: "three", from: 85 },
    { id: "stc-finale", act: "three", from: 85, to: 110 },
    { id: "stc-final-image", act: "three", from: 110 },
  ],
  freytag: [
    { id: "fr-exposition" },
    { id: "fr-inciting" },
    { id: "fr-rising" },
    { id: "fr-climax" },
    { id: "fr-falling" },
    { id: "fr-resolution" },
  ],
};

export interface StoryOutlineInput {
  structure: StoryStructure;
  title: string;
  name: string;
  description: string;
  goal: string;
  obstacle: string;
  setting: string;
  theme: string;
  /** Target length, in `unit`s. */
  length: string;
  unit: LengthUnit;
}

export interface StoryOutlineLabels {
  defaultTitle: string;
  subtitles: Record<StoryStructure, string>;
  essentialsHeading: string;
  fields: {
    protagonist: string;
    goal: string;
    obstacle: string;
    setting: string;
    theme: string;
  };
  protagonistValue: string;
  beatHeading: string;
  acts: Record<StoryAct, string>;
  actAndPosition: string;
  positions: {
    atStart: string;
    atEnd: string;
    wordAt: string;
    wordRange: string;
    wordUpTo: string;
    pageAt: string;
    pageRange: string;
    pageUpTo: string;
    percentAt: string;
    percentRange: string;
    percentUpTo: string;
    snyderAt: string;
    snyderRange: string;
  };
  beatNames: Record<string, string>;
  beatPrompts: Record<string, string>;
  fallback: {
    name: string;
    goal: string;
    obstacle: string;
    setting: string;
    theme: string;
  };
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const MAX_LENGTH = 1_000_000;

/** The target length, or null when it is missing or not a sensible whole number. */
export function storyLength(raw: string): number | null {
  const value = parseWholeCount(raw);
  if (value === null || value < 1 || value > MAX_LENGTH) {
    return null;
  }
  return value;
}

/**
 * Where a fraction of the story falls in a manuscript of `length` units: pages to the
 * nearest page, words to the nearest 100 (or 10 in a short piece) — an outline position
 * is approximate, and "word 9,818" would claim a precision it does not have.
 */
export function scalePosition(
  fraction: number,
  length: number,
  unit: LengthUnit
): number {
  const raw = fraction * length;
  if (unit === "pages") {
    return Math.max(1, Math.round(raw));
  }
  const step = length >= 10_000 ? 100 : 10;
  return Math.max(step, Math.round(raw / step) * step);
}

interface PositionContext {
  structure: StoryStructure;
  length: number | null;
  unit: LengthUnit;
  labels: StoryOutlineLabels["positions"];
  fmt: Format;
}

/** A beat's start and end as fractions of the whole story. */
function fractions(beat: BeatSpec, structure: StoryStructure) {
  const scale = structure === "save-the-cat" ? SNYDER_PAGES : 1;
  // Snyder's page 1 is the opening of the script, so it maps to the very start.
  const from =
    structure === "save-the-cat" && beat.from === 1
      ? 0
      : (beat.from ?? 0) / scale;
  return { from, to: beat.to === undefined ? undefined : beat.to / scale };
}

function scaledPosition(beat: BeatSpec, context: PositionContext): string {
  const { labels, fmt, unit } = context;
  const length = context.length as number;
  const { from, to } = fractions(beat, context.structure);
  const at = unit === "pages" ? labels.pageAt : labels.wordAt;
  const range = unit === "pages" ? labels.pageRange : labels.wordRange;
  const upTo = unit === "pages" ? labels.pageUpTo : labels.wordUpTo;
  if (to === undefined) {
    if (from <= 0) {
      return labels.atStart;
    }
    if (from >= 1) {
      return labels.atEnd;
    }
    return t(at, { n: fmt(scalePosition(from, length, unit)) });
  }
  const end = fmt(scalePosition(to, length, unit));
  if (from <= 0) {
    return t(upTo, { to: end });
  }
  return t(range, { from: fmt(scalePosition(from, length, unit)), to: end });
}

function unscaledPosition(beat: BeatSpec, context: PositionContext): string {
  const { labels, fmt } = context;
  if (context.structure === "save-the-cat") {
    return beat.to === undefined
      ? t(labels.snyderAt, { n: fmt(beat.from ?? 1) })
      : t(labels.snyderRange, { from: fmt(beat.from ?? 1), to: fmt(beat.to) });
  }
  const percent = (value: number) => fmt(value, { style: "percent" });
  const from = beat.from ?? 0;
  if (beat.to === undefined) {
    return t(labels.percentAt, { percent: percent(from) });
  }
  if (from <= 0) {
    return t(labels.percentUpTo, { to: percent(beat.to) });
  }
  return t(labels.percentRange, { from: percent(from), to: percent(beat.to) });
}

/** "around word 9,800", "pages 12–25 of Snyder's 110-page script" — or null. */
export function beatPosition(
  beat: BeatSpec,
  context: PositionContext
): string | null {
  if (beat.from === undefined) {
    return null;
  }
  return context.length === null
    ? unscaledPosition(beat, context)
    : scaledPosition(beat, context);
}

function beatGuidance(
  beat: BeatSpec,
  labels: StoryOutlineLabels,
  context: PositionContext
): DocBlock[] {
  const act = beat.act ? labels.acts[beat.act] : null;
  const position = beatPosition(beat, context);
  let text: string | null = act ?? position;
  if (act && position) {
    text = t(labels.actAndPosition, { act, position });
  }
  return text ? [{ kind: "guidance", text }] : [];
}

function essentials(
  input: StoryOutlineInput,
  labels: StoryOutlineLabels
): DocSection {
  const name = input.name.trim();
  const description = input.description.trim();
  let protagonist = name || description;
  if (name && description) {
    protagonist = t(labels.protagonistValue, { name, description });
  }
  return {
    heading: labels.essentialsHeading,
    blocks: [
      {
        kind: "fields",
        fields: [
          { label: labels.fields.protagonist, value: protagonist },
          { label: labels.fields.goal, value: input.goal.trim() },
          { label: labels.fields.obstacle, value: input.obstacle.trim() },
          { label: labels.fields.setting, value: input.setting.trim() },
          { label: labels.fields.theme, value: input.theme.trim() },
        ],
      },
    ],
  };
}

export function buildStoryOutline(
  input: StoryOutlineInput,
  labels: StoryOutlineLabels,
  fmt: Format,
  locale: string
): DocumentModel {
  const values = {
    name: input.name.trim() || labels.fallback.name,
    goal: input.goal.trim() || labels.fallback.goal,
    obstacle: input.obstacle.trim() || labels.fallback.obstacle,
    setting: input.setting.trim() || labels.fallback.setting,
    theme: input.theme.trim() || labels.fallback.theme,
  };
  const context: PositionContext = {
    structure: input.structure,
    length: storyLength(input.length),
    unit: input.unit,
    labels: labels.positions,
    fmt,
  };
  const beats: DocSection[] = STORY_BEATS[input.structure].map(
    (beat, index) => ({
      heading: t(labels.beatHeading, {
        n: index + 1,
        beat: labels.beatNames[beat.id] ?? beat.id,
      }),
      blocks: [
        ...beatGuidance(beat, labels, context),
        {
          kind: "prompt",
          text: capitalizeFirst(
            t(labels.beatPrompts[beat.id] ?? "", values),
            locale
          ),
        },
      ],
    })
  );
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: labels.subtitles[input.structure],
    sections: [essentials(input, labels), ...beats],
  };
}
